import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { PlusCircle } from 'lucide-react';
import { Subsubtype, Subtype } from '../../types/ServiceAccordion.type';
import ServiceSubSubtypeCard from './ServiceSubSubTypeCard';
import { SuccessResponse } from '../../types/CourseStageAccordion.type';
import { apiDelete } from '../../utils/apiClient';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';
import SubSubTypeDialog from '../FormDialog/SubSubtypeDialog';

interface ServiceSubSubtypeListProps {
  subtype: Subtype;
  subsubtypes: Subsubtype[];
  fetchServiceTypes: () => void;
}

const ServiceSubSubtypeList: React.FC<ServiceSubSubtypeListProps> = ({
  subtype,
  subsubtypes,
  fetchServiceTypes,
}) => {
  const [openSubSubtypeDialog, setOpenSubSubtypeDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedSubSubtype, setSelectedSubSubtype] =
    useState<Subsubtype | null>(null);

  const handleCloseSubSubtypeDialog = () => {
    setOpenSubSubtypeDialog(false);
    setSelectedSubSubtype(null);
  };

  const handleConfirmDeleteSubSubtype = async (force: boolean) => {
    try {
      const res = await apiDelete<SuccessResponse>(
        `/service/sub-subtype/delete/${selectedSubSubtype?.id}?force=${force}`,
      );

      notifyAPIClient(res.message);
      setOpenDeleteDialog(false);
      setSelectedSubSubtype(null);
      fetchServiceTypes();
    } catch (error) {
      console.error('Error deleting sub-subtype:', error);
      notifyFailure();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body2" component="h4" fontWeight="bold">
          {subtype.name} Packages
        </Typography>

        <Button
          size="medium"
          variant="contained"
          color="primary"
          startIcon={<PlusCircle size={18} />}
          sx={{
            color: 'white',
          }}
          onClick={() => {
            setSelectedSubSubtype(null);
            setOpenSubSubtypeDialog(true);
          }}
        >
          Add Package
        </Button>
      </Box>

      {subsubtypes.length === 0 ? (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ fontStyle: 'italic', py: 1 }}
        >
          No packages available
        </Typography>
      ) : (
        subsubtypes.map((subsubtype) => (
          <ServiceSubSubtypeCard
            key={subsubtype.id}
            subsubtype={subsubtype}
            onEdit={(subsubtype) => {
              setSelectedSubSubtype(subsubtype);
              setOpenSubSubtypeDialog(true);
            }}
            onDelete={(subsubtype) => {
              setSelectedSubSubtype(subsubtype);
              setOpenDeleteDialog(true);
            }}
          />
        ))
      )}

      <SubSubTypeDialog
        open={openSubSubtypeDialog}
        handleClose={handleCloseSubSubtypeDialog}
        subsubtype={selectedSubSubtype}
        subtypeId={subtype.id}
        fetchServiceTypes={fetchServiceTypes}
      />

      {/* Delete dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Package</DialogTitle>
        <DialogContent>
          Do you want to delete this package? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="contained"
            color="error"
          >
            Close
          </Button>
          <Button
            onClick={() => handleConfirmDeleteSubSubtype(false)}
            variant="contained"
            color="primary"
            className="!text-white"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceSubSubtypeList;
